// This optional code is used to register a service worker.
// register() is not called by default.
import * as Sentry from "@sentry/react";
import { type IServiceWorkerConfig, ServiceWorkerQueryParams } from "service-worker.types";

import { mobile } from "@btnet/utilities-webview";

// Keep low — failures are high-volume in prod and we only need aggregate
// signal to identify the root cause(s) before deciding whether to fix at
// source or demote the log.
const SW_REGISTRATION_FAILURE_SAMPLE_RATE = 0.05;

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

const publicUrl: string | undefined = getBaseUrl();
function getBaseUrl() {
    const publicUrl: string | undefined = import.meta.env.BASE_URL ?? "";
    return !publicUrl?.endsWith("/") ? publicUrl : publicUrl.slice(0, -1);
}

export async function register(config: IServiceWorkerConfig) {
    if (mobile.isWebview) {
        // The native mobile webview can't support the web Push/Service Worker stack, and the
        // native shell already owns push, so registering here only fails. Skip it entirely.
        return;
    }
    if (import.meta.env.VITE_DISABLE_SERVICE_WORKER === "true") {
        console.log("Service worker is disabled via environment variable.");
        return;
    }
    if (!("serviceWorker" in navigator)) {
        console.log(
            "Device is not setup to use service workers. Notifications and performance may be affected",
        );
        return;
    }

    const swUrl = getServiceWorkerUrl(config);
    const scope = `${window.location.protocol}//${window.location.hostname}`;
    const type = import.meta.env.PROD ? "classic" : "module";

    let registration: ServiceWorkerRegistration;
    try {
        registration = await navigator.serviceWorker.register(swUrl, { scope, type });
    } catch (e) {
        console.error("Error during service worker registration:", e);
        // Browsers throw SecurityError when registering a SW from an iframe (e.g. the
        // builder-website login frame). Don't report to Sentry — it's expected
        // and unactionable — but still let registration proceed in case the browser allows it.
        const isIframeSecurityError =
            window.self !== window.top && e instanceof Error && e.name === "SecurityError";
        if (!isIframeSecurityError) {
            captureRegistrationFailure(e, swUrl, scope, type);
        }
        return null;
    }

    try {
        registration.onupdatefound = () => {
            const installingWorker = registration.installing;
            if (installingWorker === null) {
                return;
            }
            installingWorker.onstatechange = () => {
                if (installingWorker.state === "installed" && navigator.serviceWorker.controller) {
                    console.log(
                        "A newer version of the service worker is detected. Replacing the current instance.",
                    );

                    // Replaces the current instance of the service worker with a new one
                    registration.waiting?.postMessage({ type: "SKIP_WAITING" });
                }
            };
        };
    } catch (e) {
        console.error("Error wiring service worker update handler:", e);
    }
    return registration;
}

// Gather environment context for service-worker registration failures so
// we can distinguish scope-mismatch, CSP, MIME, iframe, and browser-policy
// causes before deciding how to handle them long-term.
function captureRegistrationFailure(error: unknown, swUrl: URL, scope: string, type: WorkerType) {
    if (Math.random() >= SW_REGISTRATION_FAILURE_SAMPLE_RATE) {
        return;
    }
    Sentry.withScope((sentryScope) => {
        sentryScope.setLevel("warning");
        sentryScope.setTag("sw.registration", "failed");
        sentryScope.setContext("serviceWorker", {
            swUrl: swUrl.toString(),
            scope,
            type,
            isSecureContext: window.isSecureContext,
            isIframe: window.self !== window.top,
            userAgent: navigator.userAgent,
            errorName: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
        });
        Sentry.captureException(error);
    });
}

export async function get(
    config: IServiceWorkerConfig,
): Promise<ServiceWorkerRegistration | null | undefined> {
    if (mobile.isWebview) {
        // See register(): the web service worker is never installed inside the webview.
        return undefined;
    }
    if (!("serviceWorker" in navigator)) {
        return undefined;
    }

    const swUrl = getServiceWorkerUrl(config);
    return await navigator.serviceWorker.getRegistration(swUrl);
}

export async function unregister() {
    if ("serviceWorker" in navigator) {
        try {
            const registration = await navigator.serviceWorker.ready;
            await registration.unregister();
        } catch (e) {
            console.error(e);
        }
    }
}

function getServiceWorkerUrl(config: IServiceWorkerConfig) {
    const baseUrl = `${window.location.protocol}//${window.location.hostname}`;
    const swUrl = new URL(
        import.meta.env.PROD ? `${publicUrl}/service-worker.js` : `${publicUrl}/service-worker`,
        baseUrl,
    );
    swUrl.searchParams.set(ServiceWorkerQueryParams.config, JSON.stringify(config));
    return swUrl;
}
